import React, { useState } from "react";
import * as stock from "../../../services/inventoryStockService";
import useFinOptions, { optionByCode } from "../../Fin/useFinOptions";
import { DataTable, act, hint, linkBtn } from "../../Fin/gl/glUi";
import { EmptyState, FormDrawer, LoadState, PrimaryButton, SecondaryButton, cardStyle, useLoad } from "../../../components/ui";
import { TRACKING_LABELS, TYPE_LABELS, WAREHOUSE_TYPES, useInvSettings } from "./invUi";

// Inventory Settings → the inventory upgrade: what is switched on (warehouses, purchasing, who may
// buy / count, approval limit, alerts), warehouses, item classes and price levels.

const section = { ...cardStyle, padding: 16, marginTop: 16 };
const yesNo = (b) => (b ? "On" : "Off");

function Features({ s, reload }) {
  const [busy, setBusy] = useState(false);
  const [limit, setLimit] = useState(s.po_approval_limit === null ? "" : String(s.po_approval_limit));
  const [days, setDays] = useState(String(s.expiry_alert_days));
  const [months, setMonths] = useState(String(s.reorder_history_months));
  const save = async (patch) => {
    setBusy(true);
    if (await act(() => stock.saveUpgradeSettings(patch), "Saved")) reload();
    setBusy(false);
  };
  const Switch = ({ k, label, help }) => (
    <div className="d-flex align-items-start justify-content-between py-2" style={{ borderTop: "1px solid #F3F4F6" }}>
      <div className="pe-3">
        <label htmlFor={`inv-${k}`} style={{ fontWeight: 600, fontSize: 14, margin: 0 }}>
          {label}
        </label>
        <div style={hint}>{help}</div>
      </div>
      <div className="form-check form-switch">
        <input className="form-check-input" type="checkbox" role="switch" id={`inv-${k}`} checked={!!s[k]} disabled={busy || !s.can.manage} onChange={(e) => save({ [k]: e.target.checked })} />
      </div>
    </div>
  );
  return (
    <div style={section}>
      <h6 className="mb-1">Warehouses, purchasing and stock control</h6>
      <div style={hint}>Each part can be switched on when the business needs it. Nothing changes for screens you already use until you do.</div>
      <Switch k="multi_warehouse" label="Use multiple warehouses" help="Stock per warehouse with bins and min / max, transfers between warehouses, stock takes by warehouse." />
      <Switch k="purchasing_enabled" label="Use purchasing" help="Suppliers, purchase orders with approval, goods receipts (GRN), landed cost, returns to suppliers and reorder suggestions." />
      <Switch k="allow_direct_grn" label="Goods can be received without a purchase order" help="Off: every goods receipt is against an approved purchase order." />
      <Switch k="staff_can_purchase" label="Staff may raise purchase orders and receive goods" help="Owner and admins always may. Approval still follows the limit below." />
      <Switch k="staff_can_count" label="Staff may enter stock counts and lost sales" help="Posting a count stays with the owner and admins." />
      <div className="d-flex flex-wrap gap-3 pt-2" style={{ borderTop: "1px solid #F3F4F6" }}>
        <label style={{ fontSize: 14 }}>
          Purchase orders need approval from
          <input type="number" min={0} className="form-control" style={{ width: 160 }} placeholder="every order" value={limit} onChange={(e) => setLimit(e.target.value)} aria-label="Approval limit" />
        </label>
        <label style={{ fontSize: 14 }}>
          Warn about batches expiring within (days)
          <input type="number" min={0} className="form-control" style={{ width: 120 }} value={days} onChange={(e) => setDays(e.target.value)} aria-label="Expiry alert days" />
        </label>
        <label style={{ fontSize: 14 }}>
          Reorder looks at the last (months)
          <input type="number" min={1} max={24} className="form-control" style={{ width: 120 }} value={months} onChange={(e) => setMonths(e.target.value)} aria-label="Demand history months" />
        </label>
        <div className="align-self-end">
          <PrimaryButton disabled={busy || !s.can.manage} onClick={() => save({ po_approval_limit: limit === "" ? null : Number(limit), expiry_alert_days: Number(days), reorder_history_months: Number(months) })}>
            Save
          </PrimaryButton>
        </div>
      </div>
      <div style={{ ...hint, marginTop: 6 }}>
        Approval limit empty = every order is approved by the owner / an admin. Industry pack: {s.industry_pack || "General"}. Warehouses {yesNo(s.multi_warehouse)} · Purchasing {yesNo(s.purchasing_enabled)}.
      </div>
    </div>
  );
}

function Warehouses({ canManage }) {
  const { data, loading, error, reload } = useLoad(() => stock.listWarehouses(), []);
  const fin = useFinOptions(["locations"]);
  const [editing, setEditing] = useState(null);
  const locOpts = fin.locations || [];
  return (
    <div style={section}>
      <div className="d-flex align-items-center justify-content-between mb-2">
        <h6 className="mb-0">Warehouses</h6>
        {canManage ? <SecondaryButton onClick={() => setEditing({})}>Add warehouse</SecondaryButton> : null}
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        <DataTable
          rows={data || []}
          empty={<EmptyState title="No warehouses yet" text="The first one takes over today's stock." />}
          columns={[
            { label: "Code", render: (w) => <b>{w.code}</b> },
            { label: "Name", render: (w) => `${w.name}${w.is_default ? " (default)" : ""}` },
            { label: "Type", render: (w) => (WAREHOUSE_TYPES.find((t) => t.value === w.warehouse_type) || {}).label || w.warehouse_type },
            { label: "Finance location", render: (w) => w.fin_location_code || "" },
            { label: "Status", render: (w) => w.status.toLowerCase() },
            { label: "", render: (w) => (canManage ? <button type="button" style={linkBtn} onClick={() => setEditing(w)}>Edit</button> : null) },
          ]}
        />
      </LoadState>
      <FormDrawer
        show={!!editing}
        title={editing && editing.id ? `Warehouse ${editing.code}` : "Add warehouse"}
        initial={editing ? { code: editing.code || "", name: editing.name || "", warehouse_type: editing.warehouse_type || "MAIN", fin_location_id: optionByCode(locOpts, editing.fin_location_code), address: editing.address || "", is_default: !!editing.is_default, status: editing.status || "ACTIVE" } : {}}
        onHide={() => setEditing(null)}
        onDone={reload}
        onSave={(clean) => stock.saveWarehouse(editing && editing.id, clean)}
        fields={[
          { key: "code", label: "Code", required: true, max: 16 },
          { key: "name", label: "Name", required: true, max: 100 },
          { key: "warehouse_type", label: "Type", type: "select", required: true, options: WAREHOUSE_TYPES },
          ...(locOpts.length ? [{ key: "fin_location_id", label: "Finance location", type: "select", options: locOpts, hint: "Its stock is booked to this location's cost centre" }] : []),
          { key: "address", label: "Address", type: "textarea", rows: 2 },
          ...(editing && editing.id ? [{ key: "is_default", label: "Default warehouse", type: "checkbox" }, { key: "status", label: "Status", type: "select", options: [{ label: "Active", value: "ACTIVE" }, { label: "Closed", value: "INACTIVE" }] }] : []),
        ]}
      />
    </div>
  );
}

function ItemClasses({ canManage }) {
  const { data, loading, error, reload } = useLoad(() => stock.listItemClasses(), []);
  const [editing, setEditing] = useState(null);
  return (
    <div style={section}>
      <div className="d-flex align-items-center justify-content-between mb-2">
        <h6 className="mb-0">Item classes</h6>
        {canManage ? <SecondaryButton onClick={() => setEditing({})}>Add class</SecondaryButton> : null}
      </div>
      <div style={{ ...hint, marginBottom: 8 }}>A class sets an item's type and tracking when chosen, and lets Finance send its stock to its own accounts.</div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        <DataTable
          rows={data || []}
          columns={[
            { label: "Code", render: (c) => <b>{c.code}</b> },
            { label: "Name", render: (c) => `${c.name}${c.is_system ? "" : " (yours)"}` },
            { label: "Type", render: (c) => TYPE_LABELS[c.item_type] },
            { label: "Tracking", render: (c) => TRACKING_LABELS[c.tracking] },
            { label: "Shelf life", render: (c) => (c.shelf_life_days ? `${c.shelf_life_days} days` : "") },
            { label: "", render: (c) => (canManage ? <button type="button" style={linkBtn} onClick={() => setEditing(c)}>Edit</button> : null) },
          ]}
        />
      </LoadState>
      <FormDrawer
        show={!!editing}
        title={editing && editing.id ? `Class ${editing.code}` : "Add item class"}
        initial={editing ? { code: editing.code || "", name: editing.name || "", item_type: editing.item_type || "STOCK", tracking: editing.tracking || "NONE", shelf_life_days: editing.shelf_life_days || "" } : {}}
        onHide={() => setEditing(null)}
        onDone={reload}
        onSave={(clean) => stock.saveItemClass(editing && editing.id, clean)}
        fields={[
          { key: "code", label: "Code", required: true, max: 20, disabled: !!(editing && editing.is_system) },
          { key: "name", label: "Name", required: true, max: 100 },
          { key: "item_type", label: "Type", type: "select", options: Object.entries(TYPE_LABELS).map(([value, label]) => ({ value, label })) },
          { key: "tracking", label: "Tracking", type: "select", options: Object.entries(TRACKING_LABELS).map(([value, label]) => ({ value, label })) },
          { key: "shelf_life_days", label: "Shelf life (days)", type: "number", hint: "Sets the expiry from the manufacture date when a batch has none" },
        ]}
      />
    </div>
  );
}

function PriceLevels({ canManage }) {
  const { data, loading, error, reload } = useLoad(() => stock.listPriceLevels(), []);
  const [adding, setAdding] = useState(false);
  return (
    <div style={section}>
      <div className="d-flex align-items-center justify-content-between mb-2">
        <h6 className="mb-0">Price levels</h6>
        {canManage ? <SecondaryButton onClick={() => setAdding(true)}>Add level</SecondaryButton> : null}
      </div>
      <div style={{ ...hint, marginBottom: 8 }}>Retail is the item's selling price. Warranty and internal prices are used on warranty and internal workshop jobs.</div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        <DataTable rows={data || []} columns={[{ label: "Code", render: (l) => <b>{l.code}</b> }, { label: "Name", render: (l) => l.name }, { label: "Status", render: (l) => l.status.toLowerCase() }]} />
      </LoadState>
      <FormDrawer
        show={adding}
        title="Add price level"
        initial={{ code: "", name: "" }}
        onHide={() => setAdding(false)}
        onDone={reload}
        onSave={(clean) => stock.savePriceLevel(clean)}
        fields={[
          { key: "code", label: "Code", required: true, max: 20 },
          { key: "name", label: "Name", required: true, max: 100 },
        ]}
      />
    </div>
  );
}

export default function InventorySetupSections() {
  const { data: s, loading, error, reload } = useInvSettings();
  return (
    <LoadState loading={loading} error={error} onRetry={reload}>
      {s ? (
        <>
          <Features s={s} reload={reload} />
          {s.multi_warehouse ? <Warehouses canManage={s.can.manage} /> : null}
          <ItemClasses canManage={s.can.manage} />
          <PriceLevels canManage={s.can.manage} />
        </>
      ) : null}
    </LoadState>
  );
}
